import { motion } from 'framer-motion';
import { Coins, TrendingUp } from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { useConfigStore } from '../../store/useConfigStore';

interface BalanceCardProps {
  showTonEquivalent?: boolean;
}

export function BalanceCard({ showTonEquivalent = true }: BalanceCardProps) {
  const user = useAppStore((state) => state.user);
  const withdrawalConfig = useConfigStore((state) => state.withdrawal);

  if (!user) return null;

  // Calculate TON equivalent from balance
  // pointsToTonRate: how many points equal 1 TON (e.g., 5000 means 5000 points = 1 TON)
  // Ensure we always have a valid positive rate
  const rawRate = withdrawalConfig.pointsToTonRate;
  
  // Validate and normalize the rate
  let pointsPerTon: number;
  if (!rawRate || rawRate <= 0 || !Number.isFinite(rawRate)) {
    // Invalid rate, use safe default
    pointsPerTon = 100000;
  } else if (rawRate < 1) {
    // If rate is less than 1, it might be in "TON per point" format, so invert it
    pointsPerTon = 1 / rawRate;
  } else {
    // Rate is already in "points per TON" format
    pointsPerTon = rawRate;
  }
  
  // Calculate TON equivalent - ensure it's never negative
  const tonEquivalent = Math.max(0, user.balance / pointsPerTon);

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card bg-gradient-to-br from-primary-600/30 to-accent-600/30 p-6"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-white/60 text-sm mb-1">Your Balance</p>
          <div className="flex items-center gap-2">
            <Coins className="w-6 h-6 text-yellow-400" />
            <span className="text-3xl font-bold gradient-text">
              {user.balance.toLocaleString()}
            </span>
          </div>
        </div>
        <div className="text-right">
          <p className="text-white/60 text-sm mb-1">Level {user.level}</p>
          <div className="flex items-center gap-1 text-green-400">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm font-medium">
              {user.totalEarned.toLocaleString()} earned
            </span>
          </div>
        </div>
      </div>

      {showTonEquivalent && (
        <div className="flex items-center justify-between pt-4 border-t border-white/10">
          <span className="text-white/60 text-sm">TON Equivalent</span>
          <span className="text-lg font-semibold text-ton-blue">
            ≈ {tonEquivalent.toFixed(4)} TON
          </span>
        </div>
      )}

      {/* Progress to next level */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-white/60">Progress to Level {user.level + 1}</span>
          <span className="text-white/60">{user.experience} XP</span>
        </div>
        <div className="progress-bar">
          <div
            className="progress-bar-fill"
            style={{ width: `${Math.min((user.experience / 100) * 100, 100)}%` }}
          />
        </div>
      </div>
    </motion.div>
  );
}
